import { Card } from '@televet/kibble-ui';
import { Box, Flex } from '@televet/kibble-ui/build/chakra';
import { Heading } from '@televet/kibble-ui/build/components/Heading';
import { Icon } from '@televet/kibble-ui/build/components/Icon';
import { Text } from '@televet/kibble-ui/build/components/Text';
import { addMinutes } from 'date-fns';
import format from 'date-fns/format';
import parseISO from 'date-fns/parseISO';
import { openAppointmentModal } from 'pages/Appointments/state/appointmentsModalSlice';
import React from 'react';
import { getSpeciesIcon } from 'shared/components/Avatars/PetAvatar/utils';
import LoadingSpinner from 'shared/components/LoadingSpinner';
import { GraphQLFetchPolicies } from 'shared/enums/GraphQLFetchPolicies';
import { AppointmentStatus, useFindUniqueAutomationRunWithApptQuery } from 'shared/types/graphql';
import { formatTo12HourTime } from 'shared/utils/formatDate';
import { useAppDispatch } from 'state/hooks';

type TaskDetailAutomationRunAppointmentProps = {
  automationRunId?: string | null;
  borderLeftColor?: string;
  taskId?: string;
};

const TaskDetailAutomationRunAppointment = ({
  automationRunId,
  borderLeftColor,
  taskId,
}: TaskDetailAutomationRunAppointmentProps): JSX.Element => {
  const { data: automationRunData, loading } = useFindUniqueAutomationRunWithApptQuery({
    variables: {
      where: {
        id: automationRunId,
      },
    },
    fetchPolicy: GraphQLFetchPolicies.CacheFirst,
  });
  const dispatch = useAppDispatch();

  const appointment = automationRunData?.findUniqueAutomationRun?.appointment;
  const appointmentType = appointment?.appointmentType;
  const doctor = appointment?.clinicEmployees?.[0];
  const pet = appointment?.clinicPet;

  const appointmentId = appointment?.id || '';
  const automationName =
    automationRunData?.findUniqueAutomationRun?.actions?.[0]?.automationAction?.workflowEventSetting?.name || '';
  const appointmentTitle = appointmentType?.name || 'Appointment';
  const duration = appointment?.startAt
    ? formatTo12HourTime(appointment?.startAt) +
      ' - ' +
      formatTo12HourTime(addMinutes(parseISO(appointment?.startAt), appointment?.durationInMinutes || 0))
    : '';
  const startsAtStr = appointment?.startAt ? `${format(parseISO(appointment?.startAt), 'MMMM d, yyyy')}` : '';
  const petName = pet?.name || '';
  const roomName = appointment?.room?.name || '(No Room Assigned)';
  const doctorName = `${doctor?.firstName || ''} ${doctor?.lastName || ''}`.trim() || '(No Doctor Assigned)';

  if (loading) {
    return (
      <Box>
        <LoadingSpinner direction="row" />
      </Box>
    );
  }

  if (!appointment || !appointment.isActive || appointment.isDeleted) {
    return (
      <Text size="sm" fontStyle="italic">
        Could not find the appointment
      </Text>
    );
  }

  if (appointment?.status === AppointmentStatus.Cancelled) {
    return (
      <Text size="sm" fontStyle="italic">
        The appointment has been cancelled
      </Text>
    );
  }

  return (
    <Box
      className={`TaskDetailAutomationRun__${automationRunId}`}
      py={2}
      pl={3}
      borderLeftWidth="2px"
      borderLeftColor={borderLeftColor}
      width="100%"
    >
      <Box mb={4}>
        <Text size="sm" as="p" mb={1}>
          Automation
        </Text>
        <Heading size="xs" data-testid={`task-${taskId}-automationDetail-automationName`}>
          {automationName}
        </Heading>
      </Box>

      <Box>
        <Text size="sm" as="p" mb={1}>
          Appointment
        </Text>
        <Card
          data-testid={`task-${taskId}-automationDetail-appointmentCard`}
          p={3}
          borderWidth="1px"
          borderRadius="md"
          isInteractive={true}
          mr={1}
          onClick={(): void => {
            dispatch(openAppointmentModal({ appointmentId }));
          }}
        >
          <Box mb={4} background="background.info" borderRadius="sm" p={3}>
            <Text size="sm" data-testid={`task-${taskId}-automationDetail-appointmentStartsAt`}>
              {startsAtStr}
            </Text>
            <Flex gap={2} alignItems="center" mt={1}>
              <Icon name={getSpeciesIcon(pet?.speciesType, false)} size="sm" />
              <Text fontWeight="bold" data-testid={`task-${taskId}-automationDetail-appointmentPetName`}>
                {petName}
              </Text>
            </Flex>
          </Box>
          <Flex gap={3}>
            <Flex gap={2} direction="column">
              <Flex gap={2} alignItems="center">
                <Icon name="clock" size="sm" color="text.subtle" />
                <Text size="sm" data-testid={`task-${taskId}-automationDetail-appointmentDuration`}>
                  {duration}
                </Text>
              </Flex>
              <Flex gap={2} alignItems="center">
                <Icon name="calendarCheck" size="sm" color="text.subtle" />
                <Text size="sm" data-testid={`task-${taskId}-automationDetail-appointmentTitle`}>
                  {appointmentTitle}
                </Text>
              </Flex>
            </Flex>
            <Flex gap={2} direction="column">
              <Flex gap={2} alignItems="center">
                <Icon name="person" size="sm" color="text.subtle" />
                <Text size="sm" data-testid={`task-${taskId}-automationDetail-appointmentDoctorName`}>
                  {doctorName}
                </Text>
              </Flex>
              <Flex gap={2} alignItems="center">
                <Icon name="locationMarker" size="sm" color="text.subtle" />
                <Text size="sm" data-testid={`task-${taskId}-automationDetail-appointmentRoomName`}>
                  {roomName}
                </Text>
              </Flex>
            </Flex>
          </Flex>
        </Card>
      </Box>
    </Box>
  );
};

export default TaskDetailAutomationRunAppointment;
